import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Container, Grid, Stack, TextField, Typography, alpha, Divider } from '@mui/material';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import BoltIcon from '@mui/icons-material/Bolt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SyncIcon from '@mui/icons-material/Sync';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import { connectFee, CONNECT_THRESHOLD } from '../data/modules';
import { moduleColors } from '../theme';

const CONNECT_URL = 'https://connect.solvenda.org';

const benefits = [
  { icon: <BoltIcon />, title: 'No monthly fee', text: 'Free for every Solvenda provider. You only pay per successful payment.' },
  { icon: <LockIcon />, title: 'Secure card payments', text: 'Customers pay by credit or debit card on a hosted, secure page.' },
  { icon: <SyncIcon />, title: 'Synced with Solvenda', text: 'Every online payment flows straight back into your Solvenda account.' },
  { icon: <ReceiptIcon />, title: 'Receipts & support', text: 'Gateway status, store locations, receipts, support tickets and account review.' },
];

const ConnectPage = () => {
  const green = moduleColors.connect;
  const [amount, setAmount] = useState(3000);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fee = useMemo(() => connectFee(amount), [amount]);
  const net = Math.max(0, (Number(amount) || 0) - fee);

  return (
    <Box>
      <Seo
        title="SolvendaConnect | Online Card Payments for ISP Subscribers"
        description="SolvendaConnect lets your subscribers pay their ISP bill online by card at connect.solvenda.org. No monthly fee — pay only per successful payment: 50 ALL flat under 5,000 ALL, 2% + 20 ALL above."
        keywords="online payments ISP, pay internet bill online Albania, card payments ISP, SolvendaConnect, connect.solvenda.org"
        path="/connect"
      />

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.default',
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 10 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -180,
            right: -120,
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(green, 0.22)}, transparent 65%)`,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: green, bgcolor: alpha(green, 0.16) }}>
                  <CreditScoreIcon />
                </Box>
                <Typography variant="overline" sx={{ color: green }}>
                  SolvendaConnect
                </Typography>
              </Stack>
              <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
                Pay your internet bill online
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, maxWidth: 560 }}>
                Subscribers of Solvenda-powered ISPs can pay securely by card in
                seconds. For providers: no monthly fee, just a small fee per
                successful payment.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component="a"
                  href={CONNECT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                >
                  Go to payment portal
                </Button>
                <Button component={Link} to="/contact" variant="outlined" color="secondary" size="large" sx={{ color: green }}>
                  Enable for my ISP
                </Button>
              </Stack>
            </Grid>

            {/* Fee calculator card */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderColor: alpha(green, 0.4) }}>
                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Transaction fee calculator
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {`Under ${CONNECT_THRESHOLD.toLocaleString()} ALL → 50 ALL flat · ${CONNECT_THRESHOLD.toLocaleString()} ALL and above → 2% + 20 ALL`}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Payment amount (ALL)"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <Divider sx={{ my: 2.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="text.secondary">Our fee</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{fee.toLocaleString()} ALL</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">ISP receives</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: green }}>
                      {net.toLocaleString()} ALL
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container>
          <SectionHeading
            overline="Why SolvendaConnect"
            title="Online payments, the simple way"
            subtitle="A door-opener product that costs your ISP nothing per month."
          />
          <Grid container spacing={3.5}>
            {benefits.map((b, i) => (
              <Grid item xs={12} sm={6} md={3} key={b.title}>
                <Reveal delay={i * 60} sx={{ height: '100%' }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3.5 }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, color: green, bgcolor: alpha(green, 0.14) }}>
                        {b.icon}
                      </Box>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {b.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {b.text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* End-user CTA */}
      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: 'background.default' }}>
        <Container>
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              border: `1px solid ${alpha(green, 0.3)}`,
              background: `linear-gradient(135deg, ${alpha(green, 0.14)}, ${alpha(green, 0.02)})`,
            }}
          >
            <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
              Ready to pay your bill?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 560, mx: 'auto' }}>
              Head to the secure payment portal and complete your payment in seconds.
            </Typography>
            <Button
              component="a"
              href={CONNECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Open connect.solvenda.org
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ConnectPage;
